import { useEffect, useState } from 'react';

// Shared by every field so one submit with several invalid fields focuses only the first.
let lastInvalidFocusAt = 0;

const lower = (label) => (typeof label === 'string' && label ? label.toLowerCase() : '');

function messageFor(element, label, kind) {
  const name = lower(label);
  const v = element?.validity;
  if (!v || v.valueMissing) {
    if (kind === 'select') return `Please select ${name || 'an option'} to continue.`;
    return `Please enter ${name || 'this field'} to continue.`;
  }
  if (v.typeMismatch && element.type === 'email') return 'Please enter a valid email address.';
  if (v.typeMismatch && element.type === 'url') return 'Please enter a valid link, e.g. https://example.com';
  if (v.patternMismatch) return `Please check the format of ${name || 'this field'}.`;
  return element.validationMessage || 'Please check this field.';
}

/**
 * Replaces the browser's native validation bubble ("Please fill out this field.")
 * — which can't be styled — with an inline message under the field.
 * `onInvalid` goes on the real input/select; `focusRef` is what should take focus.
 */
export function useInlineValidation({ label, value, kind = 'input', focusRef, scrollRef }) {
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (String(value ?? '') !== '') setMessage('');
  }, [value]);

  const onInvalid = (event) => {
    event.preventDefault();
    setMessage(messageFor(event.target, label, kind));
    if (Date.now() - lastInvalidFocusAt > 300) {
      lastInvalidFocusAt = Date.now();
      (focusRef?.current || event.target).focus({ preventScroll: true });
      (scrollRef?.current || event.target).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return { message, onInvalid };
}

export function InlineFieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1.5 flex items-center gap-1.5 px-1 text-xs font-medium text-red-600">
      <svg className="h-3.5 w-3.5 flex-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
      </svg>
      {message}
    </p>
  );
}
